"use client";

import { useCallback, useState } from "react";
import { useToast } from "@west-tennessee-consulting/ui";
import type {
  DecisionMakerContact,
  DecisionMakerCompanyData,
  DecisionMakerImportResult,
  FindDecisionMakersStep,
} from "./types";

interface UseFindDecisionMakersModalOptions {
  onClose: () => void;
}

export function useFindDecisionMakersModal({ onClose }: UseFindDecisionMakersModalOptions) {
  const { toast } = useToast();

  const [step, setStep] = useState<FindDecisionMakersStep>("loading");
  const [contacts, setContacts] = useState<DecisionMakerContact[]>([]);
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());
  const [importResults, setImportResults] = useState<DecisionMakerImportResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [companyData, setCompanyData] = useState<DecisionMakerCompanyData | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);

  const loadDecisionMakers = useCallback(async (cId: string) => {
    if (!cId) {
      setError("Company ID not found");
      setStep("contact-selection");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch("/api/companies/find-decision-makers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ step: "load-contacts", companyId: cId }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to load decision makers");
      }

      const data = await response.json();
      setContacts(data.contacts || []);
      setCompanyData(data.companyData);
      setStep("contact-selection");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load decision makers");
      setStep("contact-selection");
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleContactSelection = useCallback((contactId: string) => {
    setSelectedContacts((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedContacts((prev) =>
      prev.size === contacts.length ? new Set() : new Set(contacts.map((c) => c.id))
    );
  }, [contacts]);

  const handleImportContacts = useCallback(async () => {
    if (selectedContacts.size === 0) {
      setError("Please select at least one contact");
      return;
    }

    if (!companyData?.id) {
      setError("Company data not available");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const contactsToImport = contacts
        .filter((c) => selectedContacts.has(c.id))
        .map(({ id, first_name, last_name, email, phone_number, job_title, job_level }) => ({
          id,
          first_name,
          last_name,
          email,
          phone_number,
          job_title,
          job_level,
        }));

      const response = await fetch("/api/companies/find-decision-makers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          step: "import-contacts",
          contacts: contactsToImport,
          companyId,
          companyData,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Import failed");
      }

      const data = await response.json();
      setImportResults(data.results || []);
      setStep("import-results");
      toast({
        title: "Import Complete",
        description: data.message,
        variant: data.failed > 0 ? "destructive" : "default",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import failed");
    } finally {
      setLoading(false);
    }
  }, [selectedContacts, contacts, companyData, companyId, toast]);

  const handleClose = useCallback(() => {
    onClose();
    setStep("loading");
    setContacts([]);
    setSelectedContacts(new Set());
    setImportResults([]);
    setError(null);
  }, [onClose]);

  return {
    step,
    setStep,
    contacts,
    selectedContacts,
    importResults,
    loading,
    error,
    companyData,
    companyId,
    setCompanyId,
    setCompanyData,
    loadDecisionMakers,
    toggleContactSelection,
    toggleSelectAll,
    handleImportContacts,
    handleClose,
  };
}
